import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Badge,
  Button,
  Form,
  Modal,
  Spinner,
} from 'react-bootstrap';
import AdminSidebar from '../../components/AdminSidebar';
import {
  AdminNotificationsProvider,
  useAdminNotifications,
} from '../../components/AdminNotifications';
import useAdminGuard from '../../hooks/useAdminGuard';
import { buildApiUrl, getApiBaseUrl } from '../../config/apiConfig';
import { hasPermission, PERMISSION_IDS } from '../../utils/permissions';
import '../../styles/admin.css';
import './AdminAsesoriaDetallePage.css';
import { logError } from '../../utils/logger';

const resolveImageUrl = (source) => {
  if (!source) return null;
  const sanitized = String(source).replace(/\\/g, '/').replace(/\s/g, '%20');
  if (/^https?:\/\//i.test(sanitized)) return sanitized;
  const base = getApiBaseUrl();
  if (sanitized.startsWith('/')) return `${base}${sanitized}`;
  if (sanitized.startsWith('uploads/')) return `${base}/${sanitized}`;
  return `${base}/uploads/${sanitized}`;
};

const AdminAsesoriaDetalleInner = () => {
  const { asesoriaId } = useParams();
  const navigate = useNavigate();
  const { token, user } = useAdminGuard();
  const { notify } = useAdminNotifications();

  // Verificar permisos
  const hasViewAsesorias = useMemo(() => hasPermission(user, PERMISSION_IDS.VER_ASESORIAS), [user]);
  const hasEditAsesorias = useMemo(() => hasPermission(user, PERMISSION_IDS.EDITAR_ASESORIAS), [user]);
  const hasDeleteAsesorias = useMemo(() => hasPermission(user, PERMISSION_IDS.ELIMINAR_ASESORIAS), [user]);

  useEffect(() => {
    if (user && !hasViewAsesorias) {
      notify({
        title: 'Acceso denegado',
        message: 'No tienes permisos para ver esta asesoría.',
        variant: 'error',
      });
      navigate('/403', { replace: true });
    }
  }, [user, hasViewAsesorias, navigate, notify]);

  const [asesoria, setAsesoria] = useState(null);
  const [profesores, setProfesores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pending, setPending] = useState(false);

  // Estados de edición
  const [editState, setEditState] = useState({
    profesor_id: { value: null, editing: false },
    usuario_id: { value: null, editing: false },
    Zoom: { value: '', editing: false },
    disponible: { value: true, editing: false },
    text: { value: '', editing: false },
    start_time: { value: '', editing: false },
    end_time: { value: '', editing: false },
    Precio: { value: 0, editing: false },
  });

  const fetchAsesoria = useCallback(async () => {
    if (!token || !asesoriaId) return;
    setLoading(true);
    try {
      const response = await fetch(buildApiUrl(`/api/asesorias/${asesoriaId}`), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`No se pudo cargar la asesoría (${response.status})`);
      }

      const data = await response.json();
      const asesoriaData = data.asesoria || data;
      setAsesoria(asesoriaData);

      // Convertir fechas para datetime-local
      const formatDateTimeLocal = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return `${year}-${month}-${day}T${hours}:${minutes}`;
      };

      setEditState({
        profesor_id: { value: asesoriaData.profesor_id || null, editing: false },
        Zoom: { value: asesoriaData.Zoom || '', editing: false },
        disponible: { value: asesoriaData.disponible ?? true, editing: false },
        text: { value: asesoriaData.text || '', editing: false },
        start_time: { value: formatDateTimeLocal(asesoriaData.start_time), editing: false },
        end_time: { value: formatDateTimeLocal(asesoriaData.end_time), editing: false },
        Precio: { value: asesoriaData.Precio || 0, editing: false },
      });
    } catch (error) {
      logError('[AdminAsesoriaDetallePage] Error fetching asesoria:', error);
      notify({
        title: 'Error al cargar la asesoría',
        message: error.message,
        variant: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, [asesoriaId, notify, token]);

  const fetchProfesores = useCallback(async () => {
    if (!token) return;
    try {
      const response = await fetch(buildApiUrl('/api/profesores'), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('No se pudieron cargar los profesores');
      }

      const data = await response.json();
      setProfesores(data.profesores || []);
    } catch (error) {
      logError('Error cargando profesores:', error);
    }
  }, [token]);

  useEffect(() => {
    fetchAsesoria();
    fetchProfesores();
  }, [fetchAsesoria, fetchProfesores]);

  const updateAsesoria = useCallback(async (updates) => {
    if (!token || !asesoriaId) return;
    setPending(true);
    try {
      // Convertir datetime-local a ISO string
      const convertToISO = (dateTimeLocal) => {
        if (!dateTimeLocal) return null;
        return new Date(dateTimeLocal).toISOString();
      };

      const updateData = { ...updates };
      if (updates.start_time !== undefined) {
        updateData.start_time = convertToISO(updates.start_time);
      }
      if (updates.end_time !== undefined) {
        updateData.end_time = convertToISO(updates.end_time);
      }

      const response = await fetch(buildApiUrl(`/api/asesorias/${asesoriaId}`), {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updateData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error al actualizar la asesoría');
      }

      await fetchAsesoria();
      return true;
    } catch (error) {
      notify({ title: 'Error', message: error.message, variant: 'error' });
      throw error;
    } finally {
      setPending(false);
    }
  }, [asesoriaId, token, notify, fetchAsesoria]);

  const handleSaveField = useCallback(async (field, value) => {
    const updateData = {};
    
    switch (field) {
      case 'profesor_id':
        updateData.profesor_id = value ? parseInt(value) : null;
        break;
      case 'Zoom':
        updateData.Zoom = value;
        break;
      case 'disponible':
        updateData.disponible = value;
        break;
      case 'text':
        updateData.text = value;
        break;
      case 'start_time':
        updateData.start_time = value ? new Date(value).toISOString() : null;
        break;
      case 'end_time':
        updateData.end_time = value ? new Date(value).toISOString() : null;
        break;
      case 'Precio':
        updateData.Precio = parseFloat(value) || 0;
        break;
      default:
        return;
    }

    try {
      await updateAsesoria(updateData);
      setEditState(prev => ({
        ...prev,
        [field]: { ...prev[field], editing: false },
      }));
      notify({
        title: 'Éxito',
        message: 'Campo actualizado correctamente',
        variant: 'success',
      });
    } catch (error) {
      // Error ya manejado en updateAsesoria
    }
  }, [updateAsesoria, notify]);

  const handleDeleteAsesoria = useCallback(async () => {
    if (!window.confirm('¿Estás seguro de que quieres eliminar esta asesoría?')) {
      return;
    }

    try {
      const response = await fetch(buildApiUrl(`/api/asesorias/${asesoriaId}`), {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Error al eliminar la asesoría');
      }

      notify({
        title: 'Éxito',
        message: 'Asesoría eliminada correctamente',
        variant: 'success',
      });
      navigate('/admin/asesorias');
    } catch (error) {
      notify({ title: 'Error', message: error.message, variant: 'error' });
    }
  }, [asesoriaId, token, notify, navigate]);

  const formatDate = (dateString) => {
    if (!dateString) return 'No definida';
    const date = new Date(dateString);
    return date.toLocaleString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
    }).format(price || 0);
  };

  if (loading) {
    return (
      <div className="admin-dashboard">
        <div className="admin-layout">
          <AdminSidebar activeKey="asesorias" />
          <main className="admin-main">
            <div className="AADP-container">
              <div className="AADP-loading">
                <Spinner animation="border" />
                <p>Cargando asesoría...</p>
              </div>
            </div>
          </main>
        </div>
      </div>
    );
  }

  if (!asesoria) {
    return (
      <div className="admin-dashboard">
        <div className="admin-layout">
          <AdminSidebar activeKey="asesorias" />
          <main className="admin-main">
            <div className="AADP-container">
              <div className="AADP-error">
                <h2>Asesoría no encontrada</h2>
                <p>La asesoría que buscas no existe o fue eliminada.</p>
                <Button variant="primary" onClick={() => navigate('/admin/asesorias')}>
                  Volver a Asesorías
                </Button>
              </div>
            </div>
          </main>
        </div>
      </div>
    );
  }

  const profesorName = asesoria.profesor
    ? `${asesoria.profesor.Nombre} ${asesoria.profesor.apellido}`
    : 'Profesor no asignado';
  const usuarioName = asesoria.usuario
    ? `${asesoria.usuario.nombre} ${asesoria.usuario.apellido}`
    : 'Usuario no asignado';

  return (
    <div className="admin-dashboard">
      <div className="admin-layout">
        <AdminSidebar activeKey="asesorias" onNavigate={(item) => navigate(item.path)} />
        <main className="admin-main">
          <div className="AADP-container">
            {/* Header */}
            <div className="AADP-header">
              <div className="AADP-header-content">
                <button
                  type="button"
                  className="AADP-back-button"
                  onClick={() => navigate('/admin/asesorias')}
                >
                  <i className="bi-arrow-left-circle" />
                  Volver
                </button>
                <h1 className="AADP-title">
                  <i className="bi-chat-dots" />
                  Detalle de Asesoría
                </h1>
                <p className="AADP-subtitle">
                  ID: {asesoria.id} • {profesorName}
                </p>
              </div>
              <div className="AADP-actions">
                <span className={`AADP-badge AADP-badge-${asesoria.disponible ? 'success' : 'secondary'}`}>
                  {asesoria.disponible ? 'Disponible' : 'No Disponible'}
                </span>
                {hasDeleteAsesorias && (
                  <button
                    type="button"
                    className="AADP-action-btn AADP-action-btn-danger"
                    onClick={handleDeleteAsesoria}
                    disabled={pending}
                  >
                    <i className="bi-trash" />
                    Eliminar
                  </button>
                )}
              </div>
            </div>

            {/* Asesoria Card */}
            <div className="AADP-asesoria-card">
              {/* Profesor Section */}
              {asesoria.profesor && (
                <div className="AADP-profesor-section">
                  {asesoria.profesor.Foto && (
                    <img
                      src={resolveImageUrl(asesoria.profesor.Foto)}
                      alt={profesorName}
                      className="AADP-profesor-image"
                    />
                  )}
                  <div className="AADP-profesor-info">
                    <h2 className="AADP-profesor-name">{profesorName}</h2>
                    {asesoria.profesor.Especialidad && (
                      <p className="AADP-profesor-specialty">{asesoria.profesor.Especialidad}</p>
                    )}
                    {asesoria.profesor.Descripcion && (
                      <p className="AADP-profesor-description">{asesoria.profesor.Descripcion}</p>
                    )}
                  </div>
                </div>
              )}

              {/* Content Section */}
              <div className="AADP-asesoria-content">
                {/* Profesor */}
                <div className="AADP-field-item">
                  <div className="AADP-field-header">
                    <label className="AADP-field-label">Profesor *</label>
                    {hasEditAsesorias && !editState.profesor_id.editing && (
                      <button
                        type="button"
                        className="AADP-field-edit-btn"
                        onClick={() => setEditState(prev => ({ ...prev, profesor_id: { ...prev.profesor_id, editing: true } }))}
                      >
                        <i className="bi-pencil" />
                      </button>
                    )}
                  </div>
                  {editState.profesor_id.editing ? (
                    <div className="AADP-field-edit">
                      <select
                        className="AADP-field-select"
                        value={editState.profesor_id.value || ''}
                        onChange={(e) => {
                          const newValue = e.target.value ? parseInt(e.target.value) : null;
                          setEditState(prev => ({
                            ...prev,
                            profesor_id: { ...prev.profesor_id, value: newValue },
                          }));
                          handleSaveField('profesor_id', newValue);
                        }}
                        onBlur={() => setEditState(prev => ({ ...prev, profesor_id: { ...prev.profesor_id, editing: false } }))}
                        autoFocus
                      >
                        <option value="">Selecciona un profesor</option>
                        {profesores.map((prof) => (
                          <option key={prof.id} value={prof.id}>
                            {prof.Nombre} {prof.apellido} {prof.Especialidad ? `- ${prof.Especialidad}` : ''}
                          </option>
                        ))}
                      </select>
                    </div>
                  ) : (
                    <div className="AADP-field-value">
                      {profesorName}
                    </div>
                  )}
                </div>

                {/* Usuario (solo lectura - se asigna al comprar) */}
                {asesoria.usuario && (
                  <div className="AADP-field-item">
                    <div className="AADP-field-header">
                      <label className="AADP-field-label">Usuario Asignado</label>
                    </div>
                    <div className="AADP-field-value">
                      {usuarioName} ({asesoria.usuario.email})
                      <span className="AADP-field-note">Asignado al comprar la asesoría</span>
                    </div>
                  </div>
                )}

                {/* Precio */}
                <div className="AADP-field-item">
                  <div className="AADP-field-header">
                    <label className="AADP-field-label">Precio (€) *</label>
                    {hasEditAsesorias && !editState.Precio.editing && (
                      <button
                        type="button"
                        className="AADP-field-edit-btn"
                        onClick={() => setEditState(prev => ({ ...prev, Precio: { ...prev.Precio, editing: true } }))}
                      >
                        <i className="bi-pencil" />
                      </button>
                    )}
                  </div>
                  {editState.Precio.editing ? (
                    <div className="AADP-field-edit">
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        className="AADP-field-input"
                        value={editState.Precio.value}
                        onChange={(e) => setEditState(prev => ({
                          ...prev,
                          Precio: { ...prev.Precio, value: parseFloat(e.target.value) || 0 },
                        }))}
                        onBlur={() => handleSaveField('Precio', editState.Precio.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            handleSaveField('Precio', editState.Precio.value);
                          } else if (e.key === 'Escape') {
                            setEditState(prev => ({ ...prev, Precio: { ...prev.Precio, editing: false } }));
                          }
                        }}
                        autoFocus
                      />
                    </div>
                  ) : (
                    <div className="AADP-field-value AADP-field-value-price">
                      {formatPrice(asesoria.Precio)}
                    </div>
                  )}
                </div>

                {/* Zoom Link */}
                <div className="AADP-field-item AADP-field-full-width">
                  <div className="AADP-field-header">
                    <label className="AADP-field-label">Link de Zoom</label>
                    {hasEditAsesorias && !editState.Zoom.editing && (
                      <button
                        type="button"
                        className="AADP-field-edit-btn"
                        onClick={() => setEditState(prev => ({ ...prev, Zoom: { ...prev.Zoom, editing: true } }))}
                      >
                        <i className="bi-pencil" />
                      </button>
                    )}
                  </div>
                  {editState.Zoom.editing ? (
                    <div className="AADP-field-edit">
                      <input
                        type="url"
                        className="AADP-field-input"
                        value={editState.Zoom.value}
                        onChange={(e) => setEditState(prev => ({
                          ...prev,
                          Zoom: { ...prev.Zoom, value: e.target.value },
                        }))}
                        onBlur={() => handleSaveField('Zoom', editState.Zoom.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            handleSaveField('Zoom', editState.Zoom.value);
                          } else if (e.key === 'Escape') {
                            setEditState(prev => ({ ...prev, Zoom: { ...prev.Zoom, editing: false } }));
                          }
                        }}
                        placeholder="https://zoom.us/j/..."
                        autoFocus
                      />
                    </div>
                  ) : (
                    <div className="AADP-field-value">
                      {asesoria.Zoom ? (
                        <a href={asesoria.Zoom} target="_blank" rel="noopener noreferrer" className="AADP-link">
                          {asesoria.Zoom}
                          <i className="bi-box-arrow-up-right ms-2" />
                        </a>
                      ) : (
                        'No definido'
                      )}
                    </div>
                  )}
                </div>

                {/* Start Time */}
                <div className="AADP-field-item">
                  <div className="AADP-field-header">
                    <label className="AADP-field-label">Fecha y Hora de Inicio</label>
                    {hasEditAsesorias && !editState.start_time.editing && (
                      <button
                        type="button"
                        className="AADP-field-edit-btn"
                        onClick={() => setEditState(prev => ({ ...prev, start_time: { ...prev.start_time, editing: true } }))}
                      >
                        <i className="bi-pencil" />
                      </button>
                    )}
                  </div>
                  {editState.start_time.editing ? (
                    <div className="AADP-field-edit">
                      <input
                        type="datetime-local"
                        className="AADP-field-input"
                        value={editState.start_time.value}
                        onChange={(e) => setEditState(prev => ({
                          ...prev,
                          start_time: { ...prev.start_time, value: e.target.value },
                        }))}
                        onBlur={() => handleSaveField('start_time', editState.start_time.value)}
                        autoFocus
                      />
                    </div>
                  ) : (
                    <div className="AADP-field-value">
                      {formatDate(asesoria.start_time)}
                    </div>
                  )}
                </div>

                {/* End Time */}
                <div className="AADP-field-item">
                  <div className="AADP-field-header">
                    <label className="AADP-field-label">Fecha y Hora de Fin</label>
                    {hasEditAsesorias && !editState.end_time.editing && (
                      <button
                        type="button"
                        className="AADP-field-edit-btn"
                        onClick={() => setEditState(prev => ({ ...prev, end_time: { ...prev.end_time, editing: true } }))}
                      >
                        <i className="bi-pencil" />
                      </button>
                    )}
                  </div>
                  {editState.end_time.editing ? (
                    <div className="AADP-field-edit">
                      <input
                        type="datetime-local"
                        className="AADP-field-input"
                        value={editState.end_time.value}
                        onChange={(e) => setEditState(prev => ({
                          ...prev,
                          end_time: { ...prev.end_time, value: e.target.value },
                        }))}
                        onBlur={() => handleSaveField('end_time', editState.end_time.value)}
                        autoFocus
                      />
                    </div>
                  ) : (
                    <div className="AADP-field-value">
                      {formatDate(asesoria.end_time)}
                    </div>
                  )}
                </div>

                {/* Text */}
                <div className="AADP-field-item AADP-field-full-width">
                  <div className="AADP-field-header">
                    <label className="AADP-field-label">Texto de Referencia</label>
                    {hasEditAsesorias && !editState.text.editing && (
                      <button
                        type="button"
                        className="AADP-field-edit-btn"
                        onClick={() => setEditState(prev => ({ ...prev, text: { ...prev.text, editing: true } }))}
                      >
                        <i className="bi-pencil" />
                      </button>
                    )}
                  </div>
                  {editState.text.editing ? (
                    <div className="AADP-field-edit">
                      <textarea
                        className="AADP-field-textarea"
                        rows={4}
                        value={editState.text.value}
                        onChange={(e) => setEditState(prev => ({
                          ...prev,
                          text: { ...prev.text, value: e.target.value },
                        }))}
                        onBlur={() => handleSaveField('text', editState.text.value)}
                        autoFocus
                      />
                    </div>
                  ) : (
                    <div className="AADP-field-value">
                      {asesoria.text || 'Sin texto de referencia'}
                    </div>
                  )}
                </div>

                {/* Disponible */}
                <div className="AADP-field-item">
                  <div className="AADP-field-header">
                    <label className="AADP-field-label">Disponible</label>
                    {hasEditAsesorias && (
                      <Form.Check
                        type="switch"
                        id="disponible-switch"
                        checked={editState.disponible.value}
                        onChange={(e) => {
                          const newValue = e.target.checked;
                          setEditState(prev => ({
                            ...prev,
                            disponible: { ...prev.disponible, value: newValue },
                          }));
                          handleSaveField('disponible', newValue);
                        }}
                      />
                    )}
                  </div>
                  {!hasEditAsesorias && (
                    <div className="AADP-field-value">
                      {asesoria.disponible ? 'Sí' : 'No'}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

const AdminAsesoriaDetallePage = () => (
  <AdminNotificationsProvider>
    <AdminAsesoriaDetalleInner />
  </AdminNotificationsProvider>
);

export default AdminAsesoriaDetallePage;

